import { z } from 'zod';

/**
 * siteFormSchema — Schema Zod para o formulário do editor de sites multi-agenda.
 *
 * Reflete exatamente o shape §6.2 de PAGES_AND_LINKS_EVOLUTION_PLAN.md:
 *   schemaVersion, siteType, layoutTemplateId, layoutMode, seo, customStyles, scope, blocks[]
 *
 * Exceção autorizada P9:
 *   blocks[].config usa z.record(z.unknown()) porque o shape varia por tipo de bloco (polimorfo).
 *   Cada editor de bloco valida seu próprio config internamente (ver blockRegistry.js / TODOs 4.5–4.17).
 *   z.record(z.unknown()) é a forma mais restritiva possível sem criar um union exaustivo dos 13 shapes.
 */

// ---------------------------------------------------------------------------
// Sub-schemas
// ---------------------------------------------------------------------------

const seoSchema = z.object({
  title: z.string().max(120, { message: 'Título SEO máx. 120 caracteres' }).optional().default(''),
  description: z.string().max(300, { message: 'Descrição SEO máx. 300 caracteres' }).optional().default(''),
  // Zod v4: .or() removido; z.record() exige dois args; string validators usam { message }
  ogImageUrl: z.union([z.string().url({ message: 'URL inválida' }), z.literal('')]).optional().default(''),
});

const customStylesSchema = z.object({
  primaryColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, { message: 'Cor primária deve ser um hex válido (#RRGGBB)' })
    .optional()
    .default('#1976d2'),
  backgroundColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, { message: 'Cor de fundo deve ser um hex válido (#RRGGBB)' })
    .optional()
    .default('#ffffff'),
  textColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, { message: 'Cor de texto deve ser um hex válido (#RRGGBB)' })
    .optional()
    .default('#212121'),
  accentColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, { message: 'Cor de destaque deve ser um hex válido (#RRGGBB)' })
    .optional()
    .default('#f50057'),
  fontFamily: z.enum(['Inter', 'Roboto', 'Poppins', 'Montserrat']).optional().default('Inter'),
  fontSize: z.enum(['small', 'medium', 'large']).optional().default('medium'),
  borderRadius: z.enum(['none', 'soft', 'round']).optional().default('soft'),
});

const scopeSchema = z.object({
  professionalId: z.string().uuid({ message: 'ID de profissional inválido' }).nullable().optional().default(null),
  placeId: z.string().uuid({ message: 'ID de local inválido' }).nullable().optional().default(null),
  // scheduleIds REMOVIDO — agendas vivem nos blocos
  // placeIds REMOVIDO — seleção explícita via placeId
});

const identitySchema = z.object({
  avatarUrl: z
    .union([z.string().url({ message: 'URL inválida' }), z.literal(''), z.null()])
    .optional()
    .default(null),
  avatarShape: z.enum(['round', 'free']).optional().default('round'),
});

// ---------------------------------------------------------------------------
// Block schema
// ---------------------------------------------------------------------------

const blockSchema = z.object({
  // UUID estável — gerado uma vez no create, nunca alterado (O8)
  id: z.string().uuid({ message: 'ID do bloco deve ser um UUID válido' }),

  type: z.enum([
    'hero',
    'about',
    'schedules',
    'services',
    'team',
    'locations',
    'experience',
    'testimonials',
    'gallery',
    'socialLinks',
    'genericLinks',
    'contact',
    'faq',
  ]),

  enabled: z.boolean().optional().default(true),

  order: z.number().int().nonnegative().optional().default(0),

  // Exceção P9 autorizada: config é polimorfo (shape varia por type).
  // Zod v4: z.record() exige DOIS argumentos (keyType, valueType).
  // z.record(z.string(), z.unknown()) aceita qualquer objeto sem coerção.
  config: z.record(z.string(), z.unknown()).optional().default({}),
});

// ---------------------------------------------------------------------------
// Root schema
// ---------------------------------------------------------------------------

export const siteFormSchema = z.object({
  schemaVersion: z.literal(2).optional().default(2),

  siteType: z.enum(['link_in_bio', 'professional_site', 'company_site'], {
    required_error: 'Tipo de site obrigatório',
  }),

  layoutTemplateId: z
    .string()
    .nullable()
    .optional()
    .default(null),

  layoutMode: z.enum(['menu', 'tabs', 'continuous']).optional().default('continuous'),

  seo: seoSchema.optional().default({}),

  customStyles: customStylesSchema.optional().default({}),

  scope: scopeSchema.optional().default({}),

  identity: identitySchema.optional().default({}),

  blocks: z.array(blockSchema).optional().default([]),
});

/**
 * Tipo inferido do schema — útil para type hints em componentes JS que usem JSDoc.
 * @typedef {z.infer<typeof siteFormSchema>} SiteFormValues
 */

export default siteFormSchema;
